import React, { useState, useEffect, useCallback } from 'react';

const POFormModal = ({ show, onClose, onSave, editingOrder, currentUser }) => {
    const [formData, setFormData] = useState({
        poNumber: '',
        supplier: '',
        orderDate: new Date().toISOString().split('T')[0],
        expectedDate: '',
        status: 'ส่งออเดอร์แล้ว',
        note: '',
        items: []
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const API_BASE = process.env.REACT_APP_API_URL || '';

    const statusOptions = [
        'ขอซื้อ',
        'ส่งออเดอร์แล้ว',
        'กำลังสั่งซื้อ',
        'จัดส่ง',
        'ถึงคลัง',
        'รับเข้าแล้ว'
    ];

    const productTypeOptions = [
        { value: 'battery', label: 'แบตเตอรี่' },
        { value: 'charger', label: 'ชาร์จเจอร์' },
        { value: 'accessory', label: 'อุปกรณ์เสริม' }
    ];

    // Fetch next PO number when modal opens for new PO
    const fetchNextPONumber = useCallback(async () => {
        try {
            const response = await fetch(`${API_BASE}/api/purchase-orders/next-number`);
            if (response.ok) {
                const data = await response.json();
                return data.poNumber;
            }
        } catch (err) {
            console.error('Error fetching next PO number:', err);
        }
        return '';
    }, [API_BASE]);

    useEffect(() => {
        if (show) {
            if (editingOrder) {
                // Edit mode
                setFormData({
                    poNumber: editingOrder.po_number,
                    supplier: editingOrder.supplier,
                    orderDate: editingOrder.order_date ? new Date(editingOrder.order_date).toISOString().split('T')[0] : '',
                    expectedDate: editingOrder.expected_date ? new Date(editingOrder.expected_date).toISOString().split('T')[0] : '',
                    status: editingOrder.status || 'ส่งออเดอร์แล้ว',
                    note: editingOrder.note || '',
                    items: editingOrder.items || []
                });
            } else {
                // New PO mode
                fetchNextPONumber().then(poNumber => {
                    setFormData({
                        poNumber: poNumber,
                        supplier: '',
                        orderDate: new Date().toISOString().split('T')[0],
                        expectedDate: '',
                        status: 'ขอซื้อ',
                        note: '',
                        items: [{ productType: 'battery', brand: '', model: '', quantity: 1 }]
                    });
                });
            }
            setError(null);
        }
    }, [show, editingOrder, fetchNextPONumber]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const addItem = () => {
        setFormData(prev => ({
            ...prev,
            items: [...prev.items, { productType: 'battery', brand: '', model: '', quantity: 1 }]
        }));
    };

    const removeItem = (index) => {
        setFormData(prev => ({
            ...prev,
            items: prev.items.filter((_, i) => i !== index)
        }));
    };

    const updateItem = (index, field, value) => {
        setFormData(prev => ({
            ...prev,
            items: prev.items.map((item, i) =>
                i === index ? { ...item, [field]: value } : item
            )
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (formData.items.length === 0) {
            setError('กรุณาเพิ่มรายการสินค้าอย่างน้อย 1 รายการ');
            return;
        }

        // Validate items
        for (let i = 0; i < formData.items.length; i++) {
            const item = formData.items[i];
            if (!item.brand || !item.model || !item.quantity) {
                setError(`กรุณากรอกข้อมูลรายการที่ ${i + 1} ให้ครบถ้วน`);
                return;
            }
        }

        setLoading(true);
        setError(null);

        try {
            const apiData = {
                po_number: formData.poNumber,
                supplier: formData.supplier,
                items: formData.items,
                order_date: formData.orderDate,
                expected_date: formData.expectedDate || null,
                status: formData.status,
                note: formData.note || null,
                created_by: currentUser?.id,
                created_by_name: currentUser?.name || currentUser?.username
            };

            const url = editingOrder
                ? `${API_BASE}/api/purchase-orders/${editingOrder.id}`
                : `${API_BASE}/api/purchase-orders`;

            const response = await fetch(url, {
                method: editingOrder ? 'PUT' : 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(apiData)
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to save purchase order');
            }

            onSave();
            onClose();
        } catch (err) {
            console.error('Error saving order:', err);
            setError(err.message || 'ไม่สามารถบันทึกข้อมูลได้');
        } finally {
            setLoading(false);
        }
    };

    if (!show) return null;

    return (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-dialog-scrollable" style={{ maxWidth: '85vw', width: '85vw' }}>
                <div className="modal-content" style={{ width: '100%', maxWidth: 'none' }}>
                    <div className="modal-header" style={{ background: 'linear-gradient(135deg, #0d6efd 0%, #0052a3 100%)', color: 'white' }}>
                        <h5 className="modal-title thai-text">
                            <i className="fas fa-clipboard-list me-2"></i>
                            {editingOrder ? 'แก้ไข Purchase Order' : 'สร้าง Purchase Order ใหม่'}
                        </h5>
                        <button type="button" className="btn-close btn-close-white" onClick={onClose}></button>
                    </div>

                    <form onSubmit={handleSubmit}>
                        <div className="modal-body" style={{ maxHeight: '75vh', overflowY: 'auto' }}>
                            {error && (
                                <div className="alert alert-danger">
                                    <i className="fas fa-exclamation-circle me-2"></i>
                                    {error}
                                </div>
                            )}

                            {/* PO Info Section */}
                            <div className="card mb-3">
                                <div className="card-header bg-light">
                                    <h6 className="mb-0 thai-text">
                                        <i className="fas fa-info-circle me-2"></i>
                                        ข้อมูล PO
                                    </h6>
                                </div>
                                <div className="card-body">
                                    <div className="row g-3">
                                        <div className="col-md-4">
                                            <label className="form-label thai-text">เลขที่ PO</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                name="poNumber"
                                                value={formData.poNumber}
                                                onChange={handleInputChange}
                                                required
                                                readOnly
                                                style={{ backgroundColor: '#f8f9fa' }}
                                            />
                                        </div>
                                        <div className="col-md-4">
                                            <label className="form-label thai-text">วันที่สั่งซื้อ</label>
                                            <input
                                                type="date"
                                                className="form-control"
                                                name="orderDate"
                                                value={formData.orderDate}
                                                onChange={handleInputChange}
                                                required
                                            />
                                        </div>
                                        <div className="col-md-4">
                                            <label className="form-label thai-text">วันที่คาดว่าจะได้รับ</label>
                                            <input
                                                type="date"
                                                className="form-control"
                                                name="expectedDate"
                                                value={formData.expectedDate}
                                                onChange={handleInputChange}
                                            />
                                        </div>
                                        <div className="col-md-6">
                                            <label className="form-label thai-text">ผู้จำหน่าย / Supplier</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                name="supplier"
                                                value={formData.supplier}
                                                onChange={handleInputChange}
                                                required
                                                placeholder="ระบุชื่อผู้จำหน่าย..."
                                            />
                                        </div>
                                        <div className="col-md-6">
                                            <label className="form-label thai-text">สถานะ</label>
                                            <select
                                                className="form-select"
                                                name="status"
                                                value={formData.status}
                                                onChange={handleInputChange}
                                            >
                                                {statusOptions.map(status => (
                                                    <option key={status} value={status}>{status}</option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Items Section */}
                            <div className="card mb-3">
                                <div className="card-header bg-light d-flex justify-content-between align-items-center">
                                    <h6 className="mb-0 thai-text">
                                        <i className="fas fa-box me-2"></i>
                                        รายการสินค้า ({formData.items.length} รายการ)
                                    </h6>
                                    <button type="button" className="btn btn-sm btn-primary" onClick={addItem}>
                                        <i className="fas fa-plus me-1"></i> เพิ่มรายการ
                                    </button>
                                </div>
                                <div className="card-body p-0">
                                    <div className="table-responsive">
                                        <table className="table table-hover mb-0">
                                            <thead className="table-dark">
                                                <tr>
                                                    <th style={{ width: '40px', minWidth: '40px' }} className="text-center">#</th>
                                                    <th style={{ minWidth: '100px' }} className="thai-text">ประเภท</th>
                                                    <th style={{ minWidth: '120px' }} className="thai-text">ยี่ห้อ</th>
                                                    <th style={{ minWidth: '150px' }} className="thai-text">รุ่น/โมเดล</th>
                                                    <th style={{ width: '80px', minWidth: '80px' }} className="thai-text text-center">จำนวน</th>
                                                    <th style={{ width: '50px', minWidth: '50px' }}></th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {formData.items.length === 0 ? (
                                                    <tr>
                                                        <td colSpan="6" className="text-center py-4 text-muted">
                                                            <i className="fas fa-box-open fa-2x mb-2"></i>
                                                            <p className="mb-0 thai-text">ยังไม่มีรายการสินค้า</p>
                                                        </td>
                                                    </tr>
                                                ) : (
                                                    formData.items.map((item, index) => (
                                                        <tr key={index}>
                                                            <td className="text-center align-middle">{index + 1}</td>
                                                            <td className="align-middle">
                                                                <select
                                                                    className="form-select form-select-sm"
                                                                    value={item.productType}
                                                                    onChange={(e) => updateItem(index, 'productType', e.target.value)}
                                                                >
                                                                    {productTypeOptions.map(opt => (
                                                                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                                                                    ))}
                                                                </select>
                                                            </td>
                                                            <td className="align-middle">
                                                                <input
                                                                    type="text"
                                                                    className="form-control form-control-sm"
                                                                    value={item.brand}
                                                                    onChange={(e) => updateItem(index, 'brand', e.target.value)}
                                                                    placeholder="ยี่ห้อ..."
                                                                    required
                                                                />
                                                            </td>
                                                            <td className="align-middle">
                                                                <input
                                                                    type="text"
                                                                    className="form-control form-control-sm"
                                                                    value={item.model}
                                                                    onChange={(e) => updateItem(index, 'model', e.target.value)}
                                                                    placeholder="รุ่น/โมเดล..."
                                                                    required
                                                                />
                                                            </td>
                                                            <td className="align-middle">
                                                                <input
                                                                    type="number"
                                                                    className="form-control form-control-sm text-center"
                                                                    value={item.quantity}
                                                                    onChange={(e) => updateItem(index, 'quantity', parseInt(e.target.value) || 1)}
                                                                    min="1"
                                                                    required
                                                                />
                                                            </td>
                                                            <td className="align-middle text-center">
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-sm btn-outline-danger"
                                                                    onClick={() => removeItem(index)}
                                                                    disabled={formData.items.length === 1}
                                                                >
                                                                    <i className="fas fa-trash"></i>
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    ))
                                                )}
                                            </tbody>
                                            {formData.items.length > 0 && (
                                                <tfoot className="table-light">
                                                    <tr>
                                                        <td colSpan="4" className="text-end thai-text fw-bold">รวมจำนวนทั้งหมด:</td>
                                                        <td className="text-center fw-bold">
                                                            {formData.items.reduce((sum, item) => sum + (parseInt(item.quantity) || 0), 0)}
                                                        </td>
                                                        <td></td>
                                                    </tr>
                                                </tfoot>
                                            )}
                                        </table>
                                    </div>
                                </div>
                            </div>

                            {/* Notes Section */}
                            <div className="mb-3">
                                <label className="form-label thai-text">หมายเหตุ</label>
                                <textarea
                                    className="form-control"
                                    name="note"
                                    value={formData.note}
                                    onChange={handleInputChange}
                                    rows="2"
                                    placeholder="หมายเหตุเพิ่มเติม (ถ้ามี)..."
                                ></textarea>
                            </div>
                        </div>

                        <div className="modal-footer">
                            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={loading}>
                                <i className="fas fa-times me-1"></i>
                                <span className="thai-text">ยกเลิก</span>
                            </button>
                            <button type="submit" className="btn btn-success" disabled={loading}>
                                {loading ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-1"></span>
                                        <span className="thai-text">กำลังบันทึก...</span>
                                    </>
                                ) : (
                                    <>
                                        <i className="fas fa-save me-1"></i>
                                        <span className="thai-text">{editingOrder ? 'บันทึกการแก้ไข' : 'สร้าง PO'}</span>
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default POFormModal;
